/**
 * KnowledgeSection — read-mostly view of the project's knowledge scope.
 *
 * Phase 1 of the "Project as ecosystem of work" track (June 7, 2026).
 * The BE columns ``projects.default_connection_ids`` and
 * ``projects.default_folder_filter`` already drive the per-chat scope:
 * every new conversation under the project inherits these arrays as the
 * ``ConversationConfig`` defaults. This section surfaces the current
 * arrays so the user can SEE what the project is biasing the agent
 * toward, and REMOVE entries inline without leaving the page.
 *
 * Scope hard-line for Phase 1:
 *   - Display the current connector ids and folder paths as chips.
 *   - Inline ``×`` on each chip to remove (one ``PATCH /projects/{id}``
 *     per click — small payload, instant feedback via TanStack
 *     invalidation).
 *   - NO add-flow yet. A dedicated multi-source picker that respects
 *     ``ConnectorFactory`` capability resolution + drive-tree traversal
 *     lands in Phase 1.5; until then the user adds connectors / folders
 *     from the composer config picker the same way they always have, and
 *     the project promotes the chosen set on the next save (Phase 2
 *     persona seed).
 *
 * Why no chip → ConnectionPicker round-trip here: the picker primitive
 * expects pre-resolved ``ConnectionPickerOption[]`` (provider, label,
 * avatar). Resolving N opaque connector ids into rich rows requires a
 * sibling fetch that doesn't exist yet at the global-connector layer
 * (the chat composer feeds it via per-capability hooks). Doing the
 * resolution well > doing it fast — see Phase 1.5 plan.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { FileText, Folder, Loader2, Plug, X } from "lucide-react";
import * as React from "react";

import type { Project } from "@ando/core/api";
import { Button, cn } from "@ando/ui";

import { useUpdateProject } from "../../hooks/useProjects.js";

export interface KnowledgeSectionProps {
  readonly project: Project;
}

/** Compact chip with an inline remove button. */
const RemovableChip: React.FC<{
  readonly label: React.ReactNode;
  readonly title?: string;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly removing: boolean;
  readonly onRemove: () => void;
  readonly removeLabel: string;
}> = ({ label, title, icon: Icon, removing, onRemove, removeLabel }) => (
  <span
    className={cn(
      "inline-flex max-w-full items-center gap-1.5 rounded-full",
      "border border-hairline bg-white/85 px-2.5 py-1",
      "text-[12px] text-ink-1",
    )}
    title={title}
  >
    <Icon className="h-3 w-3 shrink-0 text-ink-3" />
    <span className="truncate">{label}</span>
    <button
      type="button"
      onClick={onRemove}
      disabled={removing}
      aria-label={removeLabel}
      title={removeLabel}
      className={cn(
        "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
        "text-ink-3 transition-colors hover:bg-error/10 hover:text-error",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/35",
        removing && "opacity-60",
      )}
    >
      {removing ? (
        <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden />
      ) : (
        <X className="h-2.5 w-2.5" aria-hidden />
      )}
    </button>
  </span>
);

export const KnowledgeSection: React.FC<KnowledgeSectionProps> = ({ project }) => {
  const { i18n } = useLingui();
  const updateMutation = useUpdateProject();

  // We track the pending entry so two clicks in a row don't both spinner
  // the same chip — one mutation at a time keeps the UX honest.
  const [pending, setPending] = React.useState<string | null>(null);

  const connectionIds = project.default_connection_ids;
  const folderPaths = project.default_folder_filter;

  const removeConnection = React.useCallback(
    (id: string) => {
      const next = connectionIds.filter((cid) => cid !== id);
      setPending(`conn:${id}`);
      updateMutation.mutate(
        {
          projectId: project.project_id,
          patch: { default_connection_ids: next },
        },
        {
          onSettled: () => setPending(null),
        },
      );
    },
    [connectionIds, project.project_id, updateMutation],
  );

  const removeFolder = React.useCallback(
    (path: string) => {
      const next = folderPaths.filter((fp) => fp !== path);
      setPending(`folder:${path}`);
      updateMutation.mutate(
        {
          projectId: project.project_id,
          patch: { default_folder_filter: next },
        },
        {
          onSettled: () => setPending(null),
        },
      );
    },
    [folderPaths, project.project_id, updateMutation],
  );

  const empty = connectionIds.length === 0 && folderPaths.length === 0;

  return (
    <div className="flex flex-col gap-5">
      {empty ? (
        <div className="rounded-md border border-hairline border-dashed bg-card/40 px-4 py-6 text-center text-[12.5px] text-ink-2 leading-relaxed">
          <Trans>
            Nessuna fonte fissata sul progetto. Aggiungi connettori o cartelle
            dalla chat: la prossima conversazione del progetto le erediterà
            automaticamente.
          </Trans>
        </div>
      ) : null}

      {connectionIds.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="font-medium text-[12px] text-ink-2">
            <Trans>Connettori</Trans>{" "}
            <span className="ml-1 text-ink-3 tabular-nums">
              {connectionIds.length}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {connectionIds.map((id) => (
              <RemovableChip
                key={id}
                icon={Plug}
                // Opaque connector id is the best Phase 1 label until the
                // sibling resolver lands. Wrapping it in a monospace
                // ``<code>``-ish style would be louder; the tooltip
                // surfaces the full id on hover anyway.
                label={
                  <span className="font-mono text-[11px]">
                    {id.length > 14 ? `${id.slice(0, 14)}…` : id}
                  </span>
                }
                title={id}
                removing={pending === `conn:${id}`}
                onRemove={() => removeConnection(id)}
                removeLabel={i18n._(msg`Rimuovi connettore ${id.slice(0, 8)}`)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {folderPaths.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="font-medium text-[12px] text-ink-2">
            <Trans>Cartelle</Trans>{" "}
            <span className="ml-1 text-ink-3 tabular-nums">
              {folderPaths.length}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            {folderPaths.map((p) => (
              <div key={p} className="flex items-center gap-2">
                <Folder className="h-3 w-3 shrink-0 text-ink-3" aria-hidden />
                <span className="flex-1 truncate text-[12.5px] text-ink-2" title={p}>
                  {p}
                </span>
                <button
                  type="button"
                  onClick={() => removeFolder(p)}
                  disabled={pending === `folder:${p}`}
                  aria-label={i18n._(msg`Rimuovi cartella ${p}`)}
                  title={i18n._(msg`Rimuovi cartella`)}
                  className={cn(
                    "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
                    "text-ink-3 transition-colors hover:bg-error/10 hover:text-error",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/35",
                    pending === `folder:${p}` && "opacity-60",
                  )}
                >
                  {pending === `folder:${p}` ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  ) : (
                    <X className="h-3 w-3" aria-hidden />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex items-start gap-2 rounded-md border border-hairline bg-ink-1/[0.02] px-3 py-2.5 text-[11.5px] text-ink-3 leading-relaxed">
        <FileText className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
        <span>
          <Trans>
            Aggiungere nuove fonti? Apri una chat del progetto e usa il picker
            del composer: il progetto promuoverà le scelte come default nella
            prossima conversazione.
          </Trans>
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled
          title={i18n._(msg`Editor completo in arrivo nella Phase 1.5`)}
          aria-label={i18n._(msg`Aggiungi fonte (in arrivo)`)}
        >
          <Trans>Aggiungi fonte</Trans>
        </Button>
      </div>
    </div>
  );
};

KnowledgeSection.displayName = "KnowledgeSection";
